/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

export interface CantonOption {
    value: string
    label: string
    country?: string
}

// Swiss cantons in German alphabetical order, Sonstige last
const swissCantons: CantonOption[] = [
    { value: 'AG', label: 'Aargau (AG)', country: 'CH' },
    { value: 'AR', label: 'Appenzell Ausserrhoden (AR)', country: 'CH' },
    { value: 'AI', label: 'Appenzell Innerrhoden (AI)', country: 'CH' },
    { value: 'BL', label: 'Basel-Landschaft (BL)', country: 'CH' },
    { value: 'BS', label: 'Basel-Stadt (BS)', country: 'CH' },
    { value: 'BE', label: 'Bern (BE)', country: 'CH' },
    { value: 'FR', label: 'Freiburg (FR)', country: 'CH' },
    { value: 'GE', label: 'Genf (GE)', country: 'CH' },
    { value: 'GL', label: 'Glarus (GL)', country: 'CH' },
    { value: 'GR', label: 'Graubünden (GR)', country: 'CH' },
    { value: 'JU', label: 'Jura (JU)', country: 'CH' },
    { value: 'LU', label: 'Luzern (LU)', country: 'CH' },
    { value: 'NE', label: 'Neuenburg (NE)', country: 'CH' },
    { value: 'NW', label: 'Nidwalden (NW)', country: 'CH' },
    { value: 'OW', label: 'Obwalden (OW)', country: 'CH' },
    { value: 'SH', label: 'Schaffhausen (SH)', country: 'CH' },
    { value: 'SZ', label: 'Schwyz (SZ)', country: 'CH' },
    { value: 'SO', label: 'Solothurn (SO)', country: 'CH' },
    { value: 'SG', label: 'St. Gallen (SG)', country: 'CH' },
    { value: 'TI', label: 'Tessin (TI)', country: 'CH' },
    { value: 'TG', label: 'Thurgau (TG)', country: 'CH' },
    { value: 'UR', label: 'Uri (UR)', country: 'CH' },
    { value: 'VD', label: 'Waadt (VD)', country: 'CH' },
    { value: 'VS', label: 'Wallis (VS)', country: 'CH' },
    { value: 'ZG', label: 'Zug (ZG)', country: 'CH' },
    { value: 'ZH', label: 'Zürich (ZH)', country: 'CH' },
    { value: 'DE', label: 'Deutschland', country: 'DE' },
    { value: 'AT', label: 'Österreich', country: 'AT' },
    { value: 'LI', label: 'Liechtenstein', country: 'LI' },
    { value: 'Sonstige', label: 'Sonstige', country: 'CH' },
]

// German federal states (Bundesländer)
const germanStates: CantonOption[] = [
    { value: 'BW', label: 'Baden-Württemberg', country: 'DE' },
    { value: 'BY', label: 'Bayern', country: 'DE' },
    { value: 'BE', label: 'Berlin', country: 'DE' },
    { value: 'BB', label: 'Brandenburg', country: 'DE' },
    { value: 'HB', label: 'Bremen', country: 'DE' },
    { value: 'HH', label: 'Hamburg', country: 'DE' },
    { value: 'HE', label: 'Hessen', country: 'DE' },
    { value: 'MV', label: 'Mecklenburg-Vorpommern', country: 'DE' },
    { value: 'NI', label: 'Niedersachsen', country: 'DE' },
    { value: 'NW', label: 'Nordrhein-Westfalen', country: 'DE' },
    { value: 'RP', label: 'Rheinland-Pfalz', country: 'DE' },
    { value: 'SL', label: 'Saarland', country: 'DE' },
    { value: 'SN', label: 'Sachsen', country: 'DE' },
    { value: 'ST', label: 'Sachsen-Anhalt', country: 'DE' },
    { value: 'SH', label: 'Schleswig-Holstein', country: 'DE' },
    { value: 'TH', label: 'Thüringen', country: 'DE' },
    { value: 'Sonstige', label: 'Sonstige', country: 'DE' },
]

// Austrian federal states (Bundesländer)
const austrianStates: CantonOption[] = [
    { value: 'B', label: 'Burgenland', country: 'AT' },
    { value: 'K', label: 'Kärnten', country: 'AT' },
    { value: 'NÖ', label: 'Niederösterreich', country: 'AT' },
    { value: 'OÖ', label: 'Oberösterreich', country: 'AT' },
    { value: 'S', label: 'Salzburg', country: 'AT' },
    { value: 'St', label: 'Steiermark', country: 'AT' },
    { value: 'T', label: 'Tirol', country: 'AT' },
    { value: 'V', label: 'Vorarlberg', country: 'AT' },
    { value: 'W', label: 'Wien', country: 'AT' },
    { value: 'Sonstige', label: 'Sonstige', country: 'AT' },
]

// Liechtenstein (no cantons, just one entry)
const liechtensteinRegions: CantonOption[] = [
    { value: 'Liechtenstein', label: 'Liechtenstein', country: 'LI' },
    { value: 'Sonstige', label: 'Sonstige', country: 'LI' },
]

export const getCantonsByCountry = (countryCode?: string | null): CantonOption[] => {
    switch (countryCode?.toUpperCase()) {
        case 'CH':
            return swissCantons
        case 'DE':
            return germanStates
        case 'AT':
            return austrianStates
        case 'LI':
            return liechtensteinRegions
        default:
            return []
    }
}

/** Select options with explicit position so UiSelectWithGraphics keeps source order. */
export const getCantonSelectOptions = (countryCode?: string | null) =>
    getCantonsByCountry(countryCode).map((canton, index) => ({
        value: canton.value,
        label: canton.label,
        position: index,
    }))

export const getAllCantons = (): CantonOption[] => [
    ...swissCantons,
    ...germanStates,
    ...austrianStates,
    ...liechtensteinRegions,
]
